/**
 * Salary-change validation — "Karriera & Kompensimi" (frontend side).
 *
 * Frontend mirror of backend/services/salaryChange.js. Both sides must agree:
 * the UI blocks submitting an unchanged salary, and the API rejects it again if
 * a request is crafted by hand. Keep the rule AND the messages identical.
 *
 * Comparison is numeric at the STORAGE scale — worker_contracts.base_salary is
 * NUMERIC(10,2), so "1200", "1200.00" and "1200.004" are all the same money and
 * a string comparison would report a change that never happens.
 */

export const SALARY_SAME_MESSAGE         = 'Paga e re duhet të jetë e ndryshme nga paga aktuale.';
export const SALARY_EMPTY_MESSAGE        = 'Paga e propozuar mungon.';
export const SALARY_INVALID_MESSAGE      = 'Paga e propozuar nuk është numër i vlefshëm.';
export const SALARY_NOT_POSITIVE_MESSAGE = 'Paga e propozuar duhet të jetë më e madhe se zero.';

/** Decimal places worker_contracts.base_salary actually stores. */
export const SALARY_SCALE = 2;

function isThousandsGrouped(s, sep) {
  const parts = s.split(sep);
  if (parts.length === 1) return true;
  if (!/^\d{1,3}$/.test(parts[0])) return false;
  return parts.slice(1).every(p => /^\d{3}$/.test(p));
}

/**
 * Parse a salary written by a human or a machine into a Number.
 * Accepts 1200 · "1200.50" · "1200,50" · "1.200,50" · "1,200.50" · "1200 €".
 * Returns null for "", "abc", "1.2.3", "1e3" and anything else unusable.
 */
export function parseSalary(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;

  let s = v.trim();
  if (s === '') return null;

  s = s.replace(/[€$£]/g, '').replace(/\b(?:EUR|USD|GBP|CHF|ALL)\b/gi, '').trim();
  s = s.replace(/[\s  ']/g, '');
  if (s === '') return null;

  let sign = 1;
  if (s[0] === '+' || s[0] === '-') { if (s[0] === '-') sign = -1; s = s.slice(1); }
  if (s === '') return null;
  if (!/^[\d.,]+$/.test(s)) return null;

  const dots   = (s.match(/\./g) || []).length;
  const commas = (s.match(/,/g)  || []).length;

  let decimalSep = null;
  if (dots > 0 && commas > 0) {
    decimalSep = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
  } else if (dots === 1 || commas === 1) {
    const sep   = dots === 1 ? '.' : ',';
    const at    = s.indexOf(sep);
    const after = s.length - at - 1;
    const looksGrouped = after === 3 && at > 0 && s.slice(0, at) !== '0' && isThousandsGrouped(s, sep);
    decimalSep = looksGrouped ? null : sep;
  } else if (dots > 1 || commas > 1) {
    const sep = dots > 1 ? '.' : ',';
    if (!isThousandsGrouped(s, sep)) return null;
  }

  let intPart = s, fracPart = '';
  if (decimalSep) {
    const at = s.lastIndexOf(decimalSep);
    intPart  = s.slice(0, at);
    fracPart = s.slice(at + 1);
  }
  intPart = intPart.replace(/[.,]/g, '');

  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart)) return null;
  if (intPart === '' && fracPart === '') return null;

  const n = Number(`${intPart || '0'}.${fracPart || '0'}`);
  return Number.isFinite(n) ? sign * n : null;
}

/** The value as integer cents at the stored scale — the comparison key. */
export function toSalaryCents(v) {
  const n = parseSalary(v);
  if (n === null) return null;
  const cents = Number(n.toFixed(SALARY_SCALE).replace('.', ''));
  return Number.isFinite(cents) ? cents : null;
}

/** Would storing `proposed` leave base_salary exactly as it is now? */
export function isSameSalary(current, proposed) {
  const a = toSalaryCents(current);
  const b = toSalaryCents(proposed);
  if (a === null || b === null) return false;
  return a === b;
}

/**
 * Gate a proposed salary: present, parseable, greater than zero.
 * @returns {{ok: boolean, error?: string, value?: number, cents?: number}}
 */
export function validateProposedSalary(v) {
  if (v === null || v === undefined || String(v).trim() === '') {
    return { ok: false, error: SALARY_EMPTY_MESSAGE };
  }
  const cents = toSalaryCents(v);
  if (cents === null) return { ok: false, error: SALARY_INVALID_MESSAGE };
  if (cents <= 0)     return { ok: false, error: SALARY_NOT_POSITIVE_MESSAGE };
  return { ok: true, value: cents / 100, cents };
}

/** The worker's current base salary, from a selectworker payload. */
export function currentSalaryOf(worker) {
  if (!worker) return null;
  return worker.BaseSalary ?? worker.base_salary ?? null;
}

/**
 * The message to show under the salary field, or '' when it is fine.
 * Empty input returns '' — the existing "Vlera e propozuar mungon" check owns
 * that case, and nagging before the user has typed anything is noise.
 */
export function salaryFieldError(proposed, currentSalary) {
  if (proposed === null || proposed === undefined || String(proposed).trim() === '') return '';
  const v = validateProposedSalary(proposed);
  if (!v.ok) return v.error;
  return isSameSalary(currentSalary, v.value) ? SALARY_SAME_MESSAGE : '';
}
